import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { TemplateTasksTab } from './TemplateTasksTab';
import { TemplateStatusesTab } from './TemplateStatusesTab';
import { TemplateViewsTab } from './TemplateViewsTab';
import { TemplateFieldsTab } from './TemplateFieldsTab';
import { TemplateTagsTab } from './TemplateTagsTab';
import type {
  TemplateCustomField,
  TemplateGroup,
  TemplateStatus,
  TemplateTag,
  TemplateView,
} from '../types';

interface TemplateEditorSectionsProps {
  groups: TemplateGroup[];
  statuses: TemplateStatus[];
  views: TemplateView[];
  customFields: TemplateCustomField[];
  tags: TemplateTag[];
  setGroups: (updater: (groups: TemplateGroup[]) => TemplateGroup[]) => void;
  setStatuses: (updater: (statuses: TemplateStatus[]) => TemplateStatus[]) => void;
  setViews: (updater: (views: TemplateView[]) => TemplateView[]) => void;
  setCustomFields: (updater: (fields: TemplateCustomField[]) => TemplateCustomField[]) => void;
  setTags: (updater: (tags: TemplateTag[]) => TemplateTag[]) => void;
}

/** The 5 draft structure sections shared by `TemplateEditorDialog`'s "new template" flow. */
export function TemplateEditorSections({
  groups,
  statuses,
  views,
  customFields,
  tags,
  setGroups,
  setStatuses,
  setViews,
  setCustomFields,
  setTags,
}: TemplateEditorSectionsProps) {
  const { t } = useTranslation('projectTemplates');

  return (
    <Tabs defaultValue="tasks">
      <TabsList>
        <TabsTrigger value="tasks">{t('editor.sections.tasks')}</TabsTrigger>
        <TabsTrigger value="statuses">{t('editor.sections.statuses')}</TabsTrigger>
        <TabsTrigger value="views">{t('editor.sections.views')}</TabsTrigger>
        <TabsTrigger value="fields">{t('editor.sections.fields')}</TabsTrigger>
        <TabsTrigger value="tags">{t('editor.sections.tags')}</TabsTrigger>
      </TabsList>

      <TabsContent value="tasks">
        <TemplateTasksTab
          groups={groups}
          editable
          onAddGroup={(name) => setGroups((g) => [...g, { name, order: g.length, tasks: [] }])}
          onRemoveGroup={(i) => setGroups((g) => g.filter((_, idx) => idx !== i))}
          onAddTask={(groupIndex, task) =>
            setGroups((g) =>
              g.map((grp, i) => (i === groupIndex ? { ...grp, tasks: [...grp.tasks, task] } : grp)),
            )
          }
          onRemoveTask={(groupIndex, taskIndex) =>
            setGroups((g) =>
              g.map((grp, i) =>
                i === groupIndex
                  ? { ...grp, tasks: grp.tasks.filter((_, ti) => ti !== taskIndex) }
                  : grp,
              ),
            )
          }
        />
      </TabsContent>

      <TabsContent value="statuses">
        <TemplateStatusesTab
          statuses={statuses}
          editable
          onAdd={(values) => setStatuses((s) => [...s, { ...values, order: s.length }])}
          onRemove={(i) => setStatuses((s) => s.filter((_, idx) => idx !== i))}
        />
      </TabsContent>

      <TabsContent value="views">
        <TemplateViewsTab
          views={views}
          editable
          onAdd={(view) => setViews((v) => [...v, view])}
          onRemove={(i) => setViews((v) => v.filter((_, idx) => idx !== i))}
        />
      </TabsContent>

      <TabsContent value="fields">
        <TemplateFieldsTab
          fields={customFields}
          editable
          onAdd={(values) =>
            setCustomFields((f) => [...f, { ...values, options: values.options ?? null }])
          }
          onRemove={(i) => setCustomFields((f) => f.filter((_, idx) => idx !== i))}
        />
      </TabsContent>

      <TabsContent value="tags">
        <TemplateTagsTab
          tags={tags}
          editable
          onAdd={(values) => setTags((tg) => [...tg, values])}
          onRemove={(i) => setTags((tg) => tg.filter((_, idx) => idx !== i))}
        />
      </TabsContent>
    </Tabs>
  );
}
